import { useEffect } from 'react';
import { FormAsyncSelectFieldProps } from '@mews/framework';
import {
    BillingDefaultReservationsForLinkingResult,
    BillingSearchForReservationsForLinkingResult,
} from '@mews-commander/data-access';
import { useSortReservationForLinking } from '@mews-commander/legacy-entities/src/utils/useSortReservationForLinking';
import { ReservationList } from './ReservationList';
import { WithReservationChangeCallback } from './ReservationAsyncSelect';
import { renderReservationList } from './renderReservationList';
import { useGetDefaultReservationsForLinkingSuspense } from './queries';

interface ReservationAsyncSelectWithPreloadedReservationListProps
    extends Omit<
        FormAsyncSelectFieldProps<BillingSearchForReservationsForLinkingResult>,
        'useDataQuery' | 'renderData'
    > {
    enterpriseId: string;
    accountId: string;
    showOnlyUnpaidReservations: boolean;
    defaultValue?: string | null;
}

export const ReservationAsyncSelectWithPreloadedReservationList = ({
    accountId,
    enterpriseId,
    defaultValue,
    showOnlyUnpaidReservations,
    onReservationChange,
    ...props
}: ReservationAsyncSelectWithPreloadedReservationListProps & WithReservationChangeCallback) => {
    const { data } = useGetDefaultReservationsForLinkingSuspense({
        enterpriseId,
        accountId,
        showOnlyUnpaidReservations,
    });

    const sortReservationForLinking = useSortReservationForLinking();
    const reservations = (data || []).toSorted(sortReservationForLinking);

    const renderData = (rs: BillingDefaultReservationsForLinkingResult) => renderReservationList(rs, accountId);

    const defaultOptions = renderData(reservations);

    /**
     * We explicitely check for `undefined` defaultValue to allow selecting "Do not link" option as default through `null` value.
     */
    const finalDefaultValue = typeof defaultValue === 'undefined' ? reservations[0]?.reservationId : defaultValue;
    /**
     * Searching the serviceOrderId ensures the proper serviceOrder is actually loaded in the AsyncSelect through query
     * and can be displayed as selected.
     */
    const defaultSearchValue = reservations.some((r) => r.reservationId === defaultValue) ? '' : defaultValue ?? '';

    // makes sure that serviceOrderId is always set for dependent fields
    useEffect(() => {
        onReservationChange?.({ defaultValue, finalDefaultValue });
    }, [defaultValue, finalDefaultValue, onReservationChange]);

    return (
        <ReservationList
            {...props}
            renderData={renderData}
            defaultOptions={defaultOptions}
            defaultValue={finalDefaultValue}
            defaultSearchValue={defaultSearchValue}
            showOnlyUnpaidReservations={showOnlyUnpaidReservations}
        />
    );
};
